import type { CardType } from '@cencosud-cencommerce/paris-uikit';
import type { Status } from '@cencosud-cencommerce/paris-uikit/dist/atoms/Inputs/TextField/partials/TextFieldBase';
import type { Value } from '@cencosud-cencommerce/paris-uikit/dist/molecules/DatePicker/DatePicker';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import {
  addBuyNowDiscount,
  getBuyNowAvailablePaymentMethods,
  getBuyNowCart,
  getShippingGrouping,
  removeBuyNowDiscount,
  selectShippingMethod
} from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import { getBuyNowAlternativePayments } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import type { IErrorHandlerBuyNow } from '@/modules/buy-now/utils/error-handler/buy-now-error-handler.interfaces';
import type { Cart } from '@/modules/cart/domain/Cart';
import type { CartPayments } from '@/modules/cart/domain/CartPayments';
import type { IAvailablePaymentMethodsResponse } from '@/modules/perseo-billing/interfaces';
import type { HttpShippingMethodDto } from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import { TAB_PICKUP } from '@/Pages/BuyNowPage/PaymentAndShipping/constants';
import type { TabActive } from '@/Pages/BuyNowPage/PaymentAndShipping/interfaces';

type TCouponData = {
  helperText?: string;
  buttonDisabled: boolean;
  status?: Status;
  value?: string;
} | null;

export interface ClientPreference {
  shippingType?: string;
  shippingSubType?: string;
  shippingMethod?: string;
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  nodeCode?: string;
  timeSlot?: string;
}

export interface BuyNowSkuHistory {
  sku: string;
  clientPreference: ClientPreference[];
}

export interface SelectedCard {
  cardLastFourDigits: string;
  type: CardType;
  token: string;
}

interface CartState {
  data: Cart | undefined;
  isLoading?: boolean;
  error?: string;
  statusCode?: number | undefined;
  retryCount?: number;
}

interface BuyNowPaymentMethodsState {
  data: IAvailablePaymentMethodsResponse | null;
  isLoading: boolean;
  error: string | null;
}
interface GetShippingGroupingState {
  data: HttpShippingMethodDto | undefined;
  isLoading: boolean;
  error?: string;
  statusCode?: number | undefined;
  retryCount?: number;
}

interface AlternativePaymentsState {
  data: CartPayments | undefined;
  isLoading: boolean;
  error: string | undefined;
  statusCode: number | undefined;
  retryCount: number;
}

interface BuyNowState {
  isProcessingPayment: boolean;
  isSummaryOpen: boolean;
  tabActive: TabActive;
  isDatePickerOpen: boolean;
  scheduledDate: Value | null;
  cart: CartState;
  shippingGrouping: GetShippingGroupingState;
  couponData: TCouponData;
  paymentMethods: BuyNowPaymentMethodsState;
  alternativePayments: AlternativePaymentsState;
  skuHistory: BuyNowSkuHistory | null;
  paymentError: IErrorHandlerBuyNow | null;
  selectedCard: SelectedCard | null;
  isErrorFallback: boolean;
}

interface ErrorPayload {
  error?: { message?: string };
  statusCode?: number;
  retryCount?: number;
}

const DEFAULT_STATE = {
  data: undefined,
  isLoading: false,
  error: undefined,
  statusCode: undefined,
  retryCount: 0
};

const initialState: BuyNowState = {
  isProcessingPayment: false,
  isSummaryOpen: false,
  tabActive: TAB_PICKUP,
  isDatePickerOpen: false,
  scheduledDate: null,
  cart: DEFAULT_STATE,
  paymentMethods: {
    data: null,
    isLoading: false,
    error: null
  },
  shippingGrouping: DEFAULT_STATE,
  couponData: null,
  alternativePayments: DEFAULT_STATE,
  skuHistory: null,
  paymentError: null,
  selectedCard: null,
  isErrorFallback: false
};

const BuyNowSlice = createSlice({
  name: 'buyNow',
  initialState: initialState,
  reducers: {
    setIsProcessingPayment: (state, action: PayloadAction<boolean>) => {
      state.isProcessingPayment = action.payload;
    },
    setIsSummaryOpen: (state, action: PayloadAction<boolean>) => {
      state.isSummaryOpen = action.payload;
    },
    setTabActive: (state, action: PayloadAction<TabActive>) => {
      state.tabActive = action.payload;
    },
    setIsDatePickerOpen: (state, action: PayloadAction<boolean>) => {
      state.isDatePickerOpen = action.payload;
    },
    setScheduledDate: (state, action: PayloadAction<Value | null>) => {
      state.scheduledDate = action.payload;
    },
    updateBuyNowCart: (state, { payload }: PayloadAction<Partial<Cart>>) => {
      state.cart.data = {
        ...state.cart.data,
        ...(payload as Cart)
      };
    },
    setBuyNowCouponData: (state, { payload }: PayloadAction<TCouponData>) => {
      state.couponData = payload;
    },
    initializeSkuHistory: (state, { payload }: PayloadAction<string>) => {
      if (!state.skuHistory || state.skuHistory.sku !== payload) {
        state.skuHistory = {
          sku: payload,
          clientPreference: []
        };
      }
    },
    updateSkuHistory: (state, { payload }: PayloadAction<ClientPreference>) => {
      if (state.skuHistory) {
        state.skuHistory.clientPreference = [
          payload,
          ...state.skuHistory.clientPreference
        ];
      }
    },
    setBuyNowPaymentError: (
      state,
      action: PayloadAction<IErrorHandlerBuyNow | null>
    ) => {
      state.paymentError = action.payload;
    },
    setSelectedCard: (state, action: PayloadAction<SelectedCard | null>) => {
      state.selectedCard = action.payload;
    },
    setErrorFallback: (state, action: PayloadAction<boolean>) => {
      state.isErrorFallback = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBuyNowCart.pending, (state) => {
        state.cart.isLoading = true;
        state.cart.retryCount = 0;
      })
      .addCase(getBuyNowCart.fulfilled, (state, action) => {
        state.cart.isLoading = false;
        state.cart.data = action.payload;
        state.cart.error = undefined;
        state.cart.statusCode = undefined;
        state.cart.retryCount = 0;
      })
      .addCase(getBuyNowCart.rejected, (state, action) => {
        const errorPayload = action.payload as ErrorPayload;

        state.cart.isLoading = false;
        state.cart.error = errorPayload?.error?.message ?? action.error.message;
        state.cart.statusCode = errorPayload?.statusCode ?? undefined;
        state.cart.retryCount = errorPayload?.retryCount ?? 0;
      })
      .addCase(getShippingGrouping.pending, (state) => {
        state.shippingGrouping.isLoading = true;
        state.shippingGrouping.retryCount = 0;
      })
      .addCase(getShippingGrouping.fulfilled, (state, action) => {
        state.shippingGrouping.isLoading = false;
        state.shippingGrouping.data = action.payload;
        state.shippingGrouping.error = undefined;
        state.shippingGrouping.statusCode = undefined;
        state.shippingGrouping.retryCount = 0;
      })
      .addCase(getShippingGrouping.rejected, (state, action) => {
        const errorPayload = action.payload as ErrorPayload;
        state.shippingGrouping.isLoading = false;
        state.shippingGrouping.error =
          errorPayload?.error?.message ?? action.error.message;
        state.shippingGrouping.statusCode =
          errorPayload?.statusCode ?? undefined;
        state.shippingGrouping.retryCount = errorPayload?.retryCount ?? 0;
      })
      .addCase(getBuyNowAvailablePaymentMethods.pending, (state) => {
        state.paymentMethods.isLoading = true;
        state.paymentMethods.error = null;
      })
      .addCase(getBuyNowAvailablePaymentMethods.fulfilled, (state, action) => {
        state.paymentMethods.isLoading = false;
        state.paymentMethods.data = action.payload;
      })
      .addCase(getBuyNowAvailablePaymentMethods.rejected, (state, action) => {
        state.paymentMethods.isLoading = false;
        state.paymentMethods.error =
          action.payload?.message ?? action.error.message ?? null;
      })
      .addCase(addBuyNowDiscount.pending, (state) => {
        state.cart.isLoading = true;
      })
      .addCase(addBuyNowDiscount.fulfilled, (state, action) => {
        state.cart.isLoading = false;
        state.cart.data = action.payload;
      })
      .addCase(addBuyNowDiscount.rejected, (state, action) => {
        state.cart.isLoading = false;
        state.cart.error = action.error.message;
      })
      .addCase(removeBuyNowDiscount.pending, (state) => {
        state.cart.isLoading = true;
      })
      .addCase(removeBuyNowDiscount.fulfilled, (state, action) => {
        state.cart.isLoading = false;
        state.cart.data = action.payload;
      })
      .addCase(selectShippingMethod.pending, (state) => {
        state.cart.isLoading = true;
      })
      .addCase(selectShippingMethod.fulfilled, (state, action) => {
        state.cart.isLoading = false;
        state.cart.data = action.payload;
        state.cart.error = undefined;
      })
      .addCase(selectShippingMethod.rejected, (state, action) => {
        state.cart.isLoading = false;
        state.cart.error = action.error.message;
      })
      .addCase(removeBuyNowDiscount.rejected, (state, action) => {
        state.cart.isLoading = false;
        state.cart.error = action.error.message;
      })
      .addCase(getBuyNowAlternativePayments.pending, (state) => {
        state.alternativePayments.isLoading = true;
        state.alternativePayments.error = undefined;
        state.alternativePayments.retryCount = 0;
        state.alternativePayments.statusCode = undefined;
      })
      .addCase(getBuyNowAlternativePayments.fulfilled, (state, action) => {
        state.alternativePayments.isLoading = false;
        state.alternativePayments.data = action.payload;
      })
      .addCase(getBuyNowAlternativePayments.rejected, (state, action) => {
        const errorPayload = action.payload as ErrorPayload;
        state.alternativePayments.isLoading = false;
        state.alternativePayments.error =
          errorPayload?.error?.message ?? action.error.message ?? undefined;
        state.alternativePayments.statusCode =
          errorPayload?.statusCode ?? undefined;
        state.alternativePayments.retryCount = action.payload?.retryCount ?? 0;
      });
  }
});

export const { reducer: buyNowReducer, actions: BuyNowActions } = BuyNowSlice;
export const {
  setBuyNowCouponData,
  setIsProcessingPayment,
  setIsSummaryOpen,
  setTabActive,
  setIsDatePickerOpen,
  setScheduledDate,
  initializeSkuHistory,
  updateSkuHistory,
  setBuyNowPaymentError,
  setSelectedCard,
  setErrorFallback
} = BuyNowActions;
